import { Link } from 'react-router-dom'
import { Button } from '~/shadcn/ui/button/button'
import { useSettings } from '~/@mid_settings/settings'
import MobileVibrate from '~/@mid_services/mobile-vibrate/MobileVibrate'
import { PageContainer } from '~/@mid_ui/page-container/PageContainer'
import { GraphIcon } from '~/@mid_ui/icons/GraphIcon'
import { CurrencyFormatter } from '~/@mid_ui/currency-formatter/CurrencyFormatter'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'
import { HeadingThree } from '~/@mid_ui/typography/HeadingThree'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import type { UserWithMeta } from '~/@biz_account/useUser/types'
import { useUser } from '~/@biz_account/useUser/useUser'
import { useAffiliates } from './useAffiliates'
import { AffiliateTable } from './AffiliateTable'

function AffiliatePage() {
  const isDesktop = useIsDesktop()
  const { user } = useUser()
  const { setNotification } = useLocalNotifications()
  const { affiliates } = useAffiliates()
  const { getLandingPageLink } = useSettings()

  const overviewInvestors = affiliates?.length ?? 0
  const overviewAmount =
    affiliates?.reduce((acc, { userActive, bonusAffiliate }) => {
      acc += userActive ? +bonusAffiliate : 0
      return acc
    }, 0) ?? 0

  const affiliateLink = _getAffiliateLink(user ?? null)
  const copyToClipboard = () => {
    MobileVibrate.short()
    navigator.clipboard.writeText(affiliateLink).then(() => {
      setNotification('success', 'Skopírované')
    })
  }

  return (
    <>
      <PageContainer className="flex flex-col flex-shrink gap-10 w-full border-b border-border-strong pt-10 pb-15 md:pl-40 md:pr-15">
        {isDesktop && <HeadingThree>Affiliate</HeadingThree>}

        <div className="w-full flex flex-col gap-10">
          {affiliateLink ? (
            <>
              <HeadingThree className="text-2xl md:text-3xl">
                Zdieľajte odkaz a získajte affiliate bonus z rezervácií
                odporučených investorov.
              </HeadingThree>
              <div className="flex flex-col w-full gap-3 flex-shrink">
                <Button
                  onClick={copyToClipboard}
                  variant="outline"
                  className="flex w-full h-17.5 items-center flex-shrink justify-between gap-1 text-accent bg-transparent py-5 px-6 rounded-lg border-4 border-accent hover:bg-transparent">
                  <span className="w-full overflow-hidden whitespace-nowrap truncate text-start text-ellipsis">
                    {affiliateLink}
                  </span>
                  <GraphIcon className="w-6! h-6! shrink-0" />
                </Button>
                <span className="text-sm font-semibold text-muted">
                  * v prípade, že Vám Affiliate odkaz nezobrazuje, vypnite alebo
                  odinštalujte z Vášho webového prehliadača rozšírenie AdBlocker
                </span>
              </div>
              <div className="text-base font-semibold">
                INVEST IN SLOVAKIA ponúka systém odmeňovania pre našich
                investorov a ich známych. Odporučte k nám známeho a v momente
                jeho investície do našich projektov získavate obaja! &nbsp;
                <Link
                  to={getLandingPageLink('investment/affiliate')}
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent">
                  Získať viac informácií...
                </Link>
              </div>
            </>
          ) : (
            <p className="text-sm leading-[21px] font-semibold text-border">
              Affiliate odkaz nie je dostupný
            </p>
          )}
        </div>
      </PageContainer>
      <PageContainer className="flex flex-col last:grow gap-10 w-full pt-10 pb-15 md:pl-40 md:pr-15">
        <div className="flex flex-col md:flex-row flex-wrap gap-5 items-start justify-start md:gap-10 md:items-center md:justify-between">
          <span className="text-2xl font-semibold leading-9">Prehľad</span>
          <div className="w-full flex flex-row justify-between rounded-lg border border-border py-2 px-4 text-sm leading-[21px] font-semibold md:w-fit md:gap-10">
            <div className="flex flex-col md:flex-row md:items-center md:gap-2">
              <span className="md:max-w-20">Počet investorov</span>
              <span className="text-xl leading-[30px] font-semibold">
                {overviewInvestors}
              </span>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:gap-2 ">
              <span className="md:max-w-20">Získané financie</span>
              <span className="text-xl leading-[30px] font-semibold">
                <CurrencyFormatter
                  value={overviewAmount}
                  className="text-xl leading-[30px] font-semibold text-accent"
                  decimalClassName="text-base"
                />
              </span>
            </div>
          </div>
        </div>
        <AffiliateTable items={affiliates} />
      </PageContainer>
    </>
  )
}

function _getAffiliateLink(user: UserWithMeta | null) {
  return user != null && user.profile.tokenAffil
    ? location.protocol +
        '//' +
        location.hostname +
        (location.port ? ':' + location.port : '') +
        `/affiliate/${user.profile.tokenAffil}`
    : ''
}

export default AffiliatePage
